import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ThemeConstants from "../../../../config/theme/theme_constants";
import BooleanQuestion from "../components/questions/boolean_question";
import NumberQuestion from "../components/questions/number_question";
import { motion, AnimatePresence } from 'framer-motion';
import StringQuestion from "../components/questions/string_question";
import { useCreateAnswer } from "../../domain/hooks/create_answer";
import { AnswerInterface } from "../../domain/interfaces/answer_interface";
import { PulseLoader, SyncLoader } from "react-spinners";
import { useModifyAnswer } from "../../domain/hooks/modify_answer";
import { useHandleSurvey } from "../../domain/hooks/handle_survey";
import BackArrowIcon from '../../../../assets/images/back_arrow_icon.svg?react';
import { useTranslation } from '../../../../i18n/useTranslation';

const PAGE_VARIANTS = {
    enter: (direction: number) => ({
        x: direction > 0 ? window.innerWidth : -(window.innerWidth),
    }),
    center: { x: 0 },
    exit: (direction: number) => ({
        x: direction < 0 ? window.innerWidth : -(window.innerWidth),
    })
};

const PAGE_TRANSITION = {
    type: "tween" as const,
    duration: 0.2,
    ease: "easeInOut" as const,
};

const theme = ThemeConstants();

const QUESTION_COLORS = [
    theme.UvibesYellow,
    theme.UvibesGreen,
    theme.UvibesBlue,
    theme.UvibesOrange,
    theme.UvibesPink
];

const AnswerSurveyPage: React.FC = () => {
    const { t } = useTranslation();
    const { mutate: submitCreateAnswer, isPending: isCreatingAnswer } = useCreateAnswer();
    const { mutate: submitModifyAnswer, isPending: isModifyingAnswer } = useModifyAnswer();
    const { surveyId, questionId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        survey,
        isLoading: isGettingSurvey,
        error: surveyError,
        refetch,
    } = useHandleSurvey(surveyId, location.state || undefined);

    const currentIndex = useMemo(() => {
        if (!survey || !questionId) return 0;
        const idx = survey.questions.findIndex(q => q.id === questionId);
        return idx === -1 ? 0 : idx;
    }, [survey, questionId]);

    const prevIndexRef = useRef(currentIndex);
    const direction = currentIndex >= prevIndexRef.current ? 1 : -1;
    useEffect(() => {
        prevIndexRef.current = currentIndex;
    }, [currentIndex]);

    useEffect(() => {
        if (survey === null) {
            toast.error(t('survey.not_found'));
            navigate('/survey', { replace: true });
            return;
        }
        if (surveyError?.status === 400) {
            toast.error(t('survey.not_found'));
            navigate('/survey', { replace: true });
            return;
        }
        if (surveyError?.status === 403) {
            toast.error(t('survey.no_permission'));
            navigate('/survey', { replace: true });
            return;
        }
        if (!survey) return;
        const now = new Date();
        if (survey.startDate > now) {
            toast.info(t('survey.not_started'));
            navigate('/survey', { replace: true });
            return;
        }
        if (survey.endDate <= now) {
            toast.info(t('survey.ended'));
            navigate('/survey', { replace: true });
            return;
        }
        if (!survey.answered && survey.expectedRespondents > 0 && survey.nbAnswers >= survey.expectedRespondents) {
            toast.error(t('survey.quota_reached'));
            navigate('/survey', { replace: true });
            return;
        }
        if (questionId === undefined || !survey.questions.some(q => q.id === questionId)) {
            navigate(`/survey/${survey.id}/${survey.questions[0].id}`, { replace: true, state: survey });
        }
    }, [survey, surveyError, navigate, questionId, t]);

    useEffect(() => {
        if (!survey?.endDate) return;
        const ms = survey.endDate.getTime() - Date.now();
        if (ms <= 0) return;
        if (ms > 2147483647) return;
        const id = setTimeout(() => {
            toast.info(t('survey.ended'));
            navigate('/survey', { replace: true });
        }, ms);
        return () => clearTimeout(id);
    }, [survey?.endDate, navigate, t]);

    const goToPrevPage = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const goToNextPage = useCallback(() => {
        if (!survey) return;
        const newIndex = currentIndex + 1;
        if (newIndex >= survey.nbQuestions) {
            navigate(`/survey/${survey.id}/end`, { state: { survey } });
            return;
        }
        navigate(`/survey/${survey.id}/${survey.questions[newIndex].id}`, { state: survey });
    }, [navigate, survey, currentIndex]);

    const onQuestionAnswered = useCallback((answer: AnswerInterface, initialAnswer?: AnswerInterface) => {
        if (isCreatingAnswer || isModifyingAnswer || !surveyId) return;
        if (initialAnswer !== undefined) {
            if (answer.text === initialAnswer.text && answer.answer === initialAnswer.answer) {
                goToNextPage();
                return;
            }
            submitModifyAnswer({
                answer,
                answerId: initialAnswer.id!,
                surveyId,
            }, {
                onSuccess: () => goToNextPage(),
                onError: (error) => {
                    const data = error.response?.data as { message?: string } | undefined;
                    toast.error(t('survey.error_with_message', { message: data?.message ?? t('common.error.unknown') }));
                }
            });
            return;
        }
        submitCreateAnswer({ answer, surveyId }, {
            onSuccess: () => goToNextPage(),
            onError: (error) => {
                const data = error.response?.data as { message?: string } | undefined;
                const message = data?.message ?? t('common.error.unknown');
                if (message === "User has already answered this question") {
                    goToNextPage();
                    return;
                }
                toast.error(t('survey.error_with_message', { message }));
            }
        });
    }, [isCreatingAnswer, submitCreateAnswer, goToNextPage, isModifyingAnswer, submitModifyAnswer, surveyId, t]);

    const question = survey?.questions[currentIndex];
    const color = QUESTION_COLORS[currentIndex % QUESTION_COLORS.length];

    return (
        <div className={`min-h-dvh flex flex-col items-center justify-between gap-2 px-3 py-8 relative overflow-x-clip transition-colors ease-in-out duration-200`} style={{ backgroundColor: color }}>
            <div
              className="fixed w-full h-full uvibes-background-image z-0"
            />

            <header className="flex justify-center w-full items-center relative z-1">
                <BackArrowIcon width={24} height={24} className="cursor-pointer absolute start-0" onClick={goToPrevPage}/>
                <h1 className="text-white font-bold text-center">{t('survey.header')}</h1>
            </header>


            {isGettingSurvey
                ? <><SyncLoader speedMultiplier={0.5} size={24} color="white" className="z-1"/><div/></>
                : surveyError
                    ? <><div className="flex flex-col items-center gap-5 z-1">
                        <p className="text-white font-semibold text-center text-sm">{t('survey.fetch_error')}</p>
                        <button
                            className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer z-1"
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() => refetch()}
                        >
                            {t('common.button.retry')}
                        </button>
                    </div><div/></>
                    : <>
                        <div className="flex flex-col items-center gap-6 min-h-124 justify-between z-1 w-full">
                            <h2 className="text-white font-bold text-xl text-center">{t('common.question_progress', { current: currentIndex + 1, total: survey?.nbQuestions ?? 0 })}</h2>
                            <AnimatePresence initial={false} custom={direction} mode="wait">
                                <motion.div
                                  key={currentIndex}
                                  custom={direction}
                                  variants={PAGE_VARIANTS}
                                  initial="enter"
                                  animate="center"
                                  exit="exit"
                                  transition={PAGE_TRANSITION}
                                  className="w-full flex justify-center"
                                >
                                    {question && (
                                        question.type === 'boolean'
                                            ? <BooleanQuestion color={color} question={question} onValidate={onQuestionAnswered} />
                                            : question.type === 'number'
                                                ? <NumberQuestion color={color} question={question} onValidate={onQuestionAnswered}/>
                                                : <StringQuestion color={color} question={question} onValidate={onQuestionAnswered}/>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <button
                            disabled={isCreatingAnswer || isModifyingAnswer || question === undefined}
                            className={`rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 z-1 ${
                                isCreatingAnswer || isModifyingAnswer || question === undefined
                                    ? 'opacity-60 cursor-not-allowed'
                                    : 'cursor-pointer'
                            }`}
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() =>{
                                if (!isCreatingAnswer && !isModifyingAnswer && question !== undefined) {
                                    const form = document.getElementById(`question-${question.id}`);
                                    if (form instanceof HTMLFormElement) {
                                        form.requestSubmit();
                                    }
                                }
                            }}
                        >
                            {isCreatingAnswer || isModifyingAnswer ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : survey && (currentIndex + 1 < survey!.nbQuestions ? t('survey.next_question') : t('survey.finish'))}
                        </button>
                    </>
            }
        </div>
    );
}

export default AnswerSurveyPage;
